import { Controller } from "@hotwired/stimulus"

/**
 * IoT QR Scan Controller (CONEK-4142 + CONEK-4143, Plan 32 §9)
 *
 * Wires the Scan button on Iot::DeviceQrInputComponent to either:
 *   1. The native Strada barcode bridge (Turbo Native Android shell,
 *      CONEK-4143) via the `bridge--barcode` controller attached to the
 *      same element, accessed through the Stimulus outlet API.
 *   2. A browser-side modal scanner using the `html5-qrcode` package.
 *
 * On a successful scan the recognised code is written into the associated
 * input field and a `barcode:scanned` custom event is dispatched so
 * downstream Stimulus controllers (or analytics) can react.
 *
 * The browser path lazy-loads `html5-qrcode` via dynamic `import()` so the
 * desktop bundle isn't penalised when nobody opens the modal.
 *
 * Native wiring: the host element declares both controllers and an outlet
 * pointing to itself, so we resolve the sibling `bridge--barcode` instance
 * and call its `scan()` method. We also listen for the `barcode:scanned`
 * CustomEvent the bridge dispatches on its host element (same element)
 * and write the decoded value into the input.
 *
 * @example
 *   <div data-controller="iot--qr-scan bridge--barcode"
 *        data-iot--qr-scan-bridge--barcode-outlet="#input-gateway_qr-wrap"
 *        data-iot--qr-scan-input-id-value="input-gateway_qr"
 *        data-iot--qr-scan-prefix-hint-value="GW-">
 *     <input data-iot--qr-scan-target="input" />
 *     <button data-iot--qr-scan-target="trigger"
 *             data-action="click->iot--qr-scan#open">Scan</button>
 *   </div>
 *
 * Loading after a read (CONEK-9219) — opt-in, for a host that NAVIGATES on
 * the decode (the scan floating button, shared/_scan_fab). With
 * `data-iot--qr-scan-loading-on-scan-value="true"` a browser read does not
 * close the modal: the camera stops, its area becomes a loading state
 * (`role="status"`, the spinner from the `loadingTemplate` target + the
 * `loading` text), × / Esc / the backdrop stop working, and no further code is
 * read. The modal goes away with the page: on `turbo:before-cache` (so Back
 * never restores a spinner), on `turbo:before-render` (pages that are not
 * cached never fire before-cache), and on a back/forward-cache `pageshow`
 * (the full-page-load fallback). A failed visit (`turbo:fetch-request-error`)
 * or no page after `loadingTimeout` ms closes it with the `errorNavigate`
 * note. A host that ends up not navigating calls `stopLoading()`.
 * Without the value (IoT pairing fills an input and stays) nothing changes:
 * the modal closes at once.
 */
export default class extends Controller {
  static targets = ["input", "trigger", "loadingTemplate"]
  static outlets = ["bridge--barcode"]
  static values = {
    inputId: String,
    prefixHint: String,
    label: String,
    // Translated by the host component; fallbacks cover markup without them.
    title: { type: String, default: "Scan QR code" },
    instruction: {
      type: String,
      default: "Position the code inside the frame.",
    },
    closeLabel: { type: String, default: "Close scanner" },
    errorNative: {
      type: String,
      default: "Native scanner unavailable. Please type the code instead.",
    },
    errorStart: {
      type: String,
      default: "Scanner could not start. Please type the code instead.",
    },
    errorCamera: {
      type: String,
      default: "Could not open the camera. Please type the code instead.",
    },
    // CONEK-9219 — loading state after a read, for hosts that navigate.
    loadingOnScan: { type: Boolean, default: false },
    loading: { type: String, default: "Opening…" },
    errorNavigate: {
      type: String,
      default:
        "The page could not be opened. Check your connection and try again.",
    },
    loadingTimeout: { type: Number, default: 15000 },
  }

  connect() {
    this.cleanupScanner = null
    this.modal = null
    this.opening = false
    this.host = null
    this.hiddenByScanner = []
    this.onEscape = null
    this.onHostClose = null
    this.parts = null
    this.isLoading = false
    this.loadingTimer = null
    this.onLoadingEnd = () => this.closeModal({ restoreFocus: false })
    this.onLoadingFailed = () => this.failLoading()
    this.onLoadingPageShow = (event) => {
      if (event.persisted) this.closeModal({ restoreFocus: false })
    }
    this.onLoadingKeydown = this.handleLoadingKeydown.bind(this)
    this.onBarcodeScanned = this.handleBarcodeScanned.bind(this)
    // The bridge dispatches `barcode:scanned` on its host element, which is
    // the same element this controller is attached to — so we can listen
    // locally rather than at document level.
    this.element.addEventListener("barcode:scanned", this.onBarcodeScanned)
  }

  disconnect() {
    this.element.removeEventListener("barcode:scanned", this.onBarcodeScanned)
    this.closeModal()
  }

  /**
   * Click handler on the Scan button.
   *
   * Route on the CONNECTED bridge controller, not `hasBridgeBarcodeOutlet`:
   * that Stimulus helper is attribute-only — it is true whenever the element
   * declares `bridge--barcode` (which DeviceQrInputComponent always does),
   * even in a mobile browser where the controller never loads because its
   * `shouldLoad` sees no `bridge-components:` token in the UA. Trusting it
   * made the outlet getter throw and always showed "Native scanner
   * unavailable", leaving the html5-qrcode fallback unreachable. The plural
   * getter warns and filters instead of throwing, so a mobile browser lands
   * in `openBrowser()`.
   */
  open(event) {
    event?.preventDefault?.()

    const bridge = this.bridgeBarcodeOutlets[0]
    if (bridge) {
      this.openNative(bridge)
    } else {
      this.openBrowser()
    }
  }

  openNative(bridge) {
    try {
      bridge.scan({
        label: this.hasLabelValue ? this.labelValue : "",
        expected_format: "QR_CODE",
      })
    } catch (err) {
      this.showFriendlyError(this.errorNativeValue)
      console.warn("iot--qr-scan native bridge failed:", err)
    }
  }

  /**
   * Receiver for the `barcode:scanned` CustomEvent dispatched by the
   * `bridge--barcode` controller after the native ZXing reply lands.
   * `detail.error` is set for cancellation / no_activity / no_registry — we
   * exit quietly in that case so the user can re-tap Scan or type the code.
   *
   * We deliberately ignore events that bubble up from our own
   * [applyScan] re-dispatch on the input (those carry `detail.source ===
   * "iot--qr-scan"`) to avoid an infinite-recursion loop.
   *
   * CONEK-9219 — no loading state on this path, on purpose: `loadingOnScan`
   * belongs to the browser-camera modal only. The native scanner draws its
   * own chrome and closes itself, so there is no web dialog to turn into a
   * loading state here.
   */
  handleBarcodeScanned(event) {
    const detail = event?.detail || {}
    if (detail.source === "iot--qr-scan") return // own re-dispatch, ignore
    if (detail.error) {
      // "cancelled" is the common path (user dismissed the camera) — silent.
      if (detail.error !== "cancelled") {
        console.warn("iot--qr-scan native bridge error:", detail.error)
        this.showFriendlyError(this.errorStartValue)
      }
      return
    }
    if (typeof detail.value === "string" && detail.value.length > 0) {
      this.applyScan(detail.value)
    }
  }

  async openBrowser() {
    // A second tap while the chunk is still loading (or while the scanner is
    // up) must not build a second scanner: the first one's camera and dialog
    // bookkeeping would be orphaned.
    if (this.opening || this.modal) return
    this.opening = true
    try {
      const { Html5Qrcode } = await import("html5-qrcode")
      this.renderModal()
      const modal = this.modal

      const region = modal.querySelector("[data-qr-scan-region]")
      const scanner = new Html5Qrcode(region.id)
      const cleanup = async () => {
        try {
          await scanner.stop()
        } catch (_e) {
          /* already stopped */
        }
        try {
          await scanner.clear()
        } catch (_e) {
          /* already cleared */
        }
      }
      this.cleanupScanner = cleanup

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          // The camera keeps decoding (10 fps) until it is stopped: one read
          // per scan once the loading state has started.
          if (this.isLoading) return
          if (this.loadingOnScanValue) {
            await this.startLoading()
            if (this.isLoading) this.applyScan(decodedText)
            return
          }
          // Give the field back BEFORE filling it: restoring the dialog hands
          // focus to the Scan button, and the scan should leave it on the input.
          await this.closeModal()
          this.applyScan(decodedText)
        },
        () => {
          /* per-frame scan errors are noisy — ignore */
        },
      )

      // Closed while the camera permission prompt was still pending: the first
      // teardown ran before the stream existed (stop() threw "not running" and
      // was swallowed), so it did not turn the camera off. This second one does.
      // Deliberately NOT deduplicated with a "done" flag: that would skip it.
      if (this.modal !== modal) await cleanup()
    } catch (err) {
      console.warn("iot--qr-scan browser scanner failed:", err)
      // Give the field back first: leaving its dialog hidden behind an empty
      // scanner would strand the user on a black box.
      await this.closeModal()
      this.showFriendlyError(this.errorCameraValue)
    } finally {
      this.opening = false
    }
  }

  applyScan(text) {
    if (!this.hasInputTarget) return
    this.inputTarget.value = text
    // `source: "iot--qr-scan"` lets [handleBarcodeScanned] distinguish this
    // downstream notification from the bridge's reply event (otherwise the
    // bubbled-up event would cause infinite recursion).
    this.inputTarget.dispatchEvent(
      new CustomEvent("barcode:scanned", {
        bubbles: true,
        detail: { value: text, source: "iot--qr-scan" },
      }),
    )
    // While loading, focus stays on the loading state inside the modal.
    if (!this.isLoading) this.inputTarget.focus()
  }

  /**
   * CONEK-9219 — the read is done and the host is about to navigate: stop the
   * camera, swap its area for the loading state and wait for the page.
   * `isLoading` is set before the first await so the camera's next frames are
   * dropped.
   */
  async startLoading() {
    this.isLoading = true
    if (this.cleanupScanner) {
      const cleanup = this.cleanupScanner
      this.cleanupScanner = null
      await cleanup()
    }
    // Closed (or the page left) while the camera was stopping.
    if (!this.isLoading || !this.modal || !this.parts) return

    const { region, caption, closeBtn } = this.parts
    const status = this.buildLoading()
    region.replaceWith(status)
    caption.remove()
    closeBtn.hidden = true

    document.addEventListener("turbo:before-cache", this.onLoadingEnd)
    document.addEventListener("turbo:before-render", this.onLoadingEnd)
    document.addEventListener("turbo:fetch-request-error", this.onLoadingFailed)
    window.addEventListener("pageshow", this.onLoadingPageShow)
    document.addEventListener("keydown", this.onLoadingKeydown, true)
    this.loadingTimer = setTimeout(
      this.onLoadingFailed,
      this.loadingTimeoutValue,
    )

    status.focus({ preventScroll: true })
  }

  /** For a host that will not navigate after all (nothing to visit). */
  stopLoading() {
    return this.closeModal({ restoreFocus: false })
  }

  async failLoading() {
    await this.closeModal({ restoreFocus: false })
    this.showFriendlyError(this.errorNavigateValue)
  }

  endLoading() {
    if (!this.isLoading) return
    this.isLoading = false
    clearTimeout(this.loadingTimer)
    this.loadingTimer = null
    document.removeEventListener("turbo:before-cache", this.onLoadingEnd)
    document.removeEventListener("turbo:before-render", this.onLoadingEnd)
    document.removeEventListener(
      "turbo:fetch-request-error",
      this.onLoadingFailed,
    )
    window.removeEventListener("pageshow", this.onLoadingPageShow)
    document.removeEventListener("keydown", this.onLoadingKeydown, true)
  }

  // Nothing in the modal can take focus while loading (× is hidden), so Tab
  // would leave it for the page behind, and Escape must not close it.
  handleLoadingKeydown(event) {
    if (event.key !== "Tab" && event.key !== "Escape") return
    event.preventDefault()
    event.stopPropagation()
    this.modal?.querySelector("[data-qr-scan-loading]")?.focus({
      preventScroll: true,
    })
  }

  // The square the camera had, so the modal does not change size. The spinner
  // is Ui::SpinnerComponent from the host's <template>; the text is announced.
  buildLoading() {
    const status = document.createElement("div")
    status.setAttribute("data-qr-scan-loading", "")
    status.setAttribute("role", "status")
    status.setAttribute("aria-live", "polite")
    status.tabIndex = -1
    status.className =
      "w-full aspect-square rounded bg-muted/40 flex flex-col items-center " +
      "justify-center gap-3 outline-none"

    const spinner = this.hasLoadingTemplateTarget
      ? this.loadingTemplateTarget.content.firstElementChild?.cloneNode(true)
      : null
    status.appendChild(spinner || this.fallbackSpinner())

    const text = document.createElement("p")
    text.className = "text-sm text-foreground"
    text.textContent = this.loadingValue
    status.appendChild(text)
    return status
  }

  fallbackSpinner() {
    const spinner = document.createElement("span")
    spinner.setAttribute("aria-hidden", "true")
    spinner.className =
      "h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"
    return spinner
  }

  renderModal() {
    // Build the modal exclusively with safe DOM methods (no innerHTML).
    // No part of the markup interpolates untrusted input, but using
    // createElement + textContent makes that property locally enforced
    // rather than something a future edit could quietly break.
    this.modal = document.createElement("div")
    this.modal.setAttribute("role", "dialog")
    this.modal.setAttribute("aria-modal", "true")
    this.modal.className =
      "fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"

    const dialog = document.createElement("div")
    dialog.className =
      "bg-card rounded-lg shadow-xl w-full max-w-sm p-4 flex flex-col gap-3"

    const header = document.createElement("div")
    header.className = "flex items-center justify-between"

    const heading = document.createElement("h3")
    heading.className = "text-base font-semibold text-foreground"
    heading.textContent = this.titleValue

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.setAttribute("data-qr-scan-close", "")
    closeBtn.className =
      "min-h-11 min-w-11 inline-flex items-center justify-center rounded text-foreground-medium hover:bg-muted/50"
    closeBtn.setAttribute("aria-label", this.closeLabelValue)
    closeBtn.textContent = "×" // multiplication sign, visually "×"

    header.appendChild(heading)
    header.appendChild(closeBtn)

    const region = document.createElement("div")
    region.id = `qr-region-${Math.random().toString(36).slice(2, 10)}`
    region.setAttribute("data-qr-scan-region", "")
    // CONEK-8571 — object-cover on the inner <video> so a non-square camera
    // feed (most webcams deliver 4:3 or 16:9) FILLS the square region
    // instead of letterboxing with a black band. The band was especially
    // visible from the global palette's larger dialog; the reticle overlay
    // sits on the region, so with cover the brackets stay anchored to the
    // actual visible video area. html5-qrcode may size the <video> with
    // inline attrs (width/height), hence `!` on object-cover.
    region.className =
      "w-full aspect-square bg-black rounded overflow-hidden " +
      "[&_video]:w-full [&_video]:h-full [&_video]:!object-cover"

    const caption = document.createElement("p")
    caption.className = "text-xs text-foreground-medium text-center"
    caption.textContent = this.instructionValue

    dialog.appendChild(header)
    dialog.appendChild(region)
    dialog.appendChild(caption)
    this.modal.appendChild(dialog)

    this.parts = { region, caption, closeBtn }
    this.mountModal()
    closeBtn.addEventListener("click", () => {
      if (!this.isLoading) this.closeModal()
    })
    closeBtn.focus()
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal && !this.isLoading) this.closeModal()
    })
  }

  /**
   * Put the scanner on screen. Inside a Ui::ModalComponent the field lives in a
   * <dialog> opened with showModal(): that puts the dialog in the browser's top
   * layer and makes everything outside it inert, so a scanner appended to
   * <body> ends up UNDERNEATH it and unreachable (CONEK-8085). There the
   * scanner is mounted inside the dialog instead, and the dialog's own content
   * is hidden for as long as it is up, so one dialog is visible at a time.
   * Anywhere else (a page, a drawer) it still goes on <body>.
   */
  mountModal() {
    const host = this.element.closest("dialog")
    if (!host) {
      document.body.appendChild(this.modal)
      return
    }

    this.host = host
    this.hiddenByScanner = Array.from(host.children).filter(
      (child) => !child.hidden,
    )
    this.hiddenByScanner.forEach((child) => {
      child.hidden = true
    })
    host.appendChild(this.modal)

    // Escape cancels the SCAN and returns to the dialog, like × does. ui--modal
    // (conek-ui gem) listens for Escape on `document` in the BUBBLE phase
    // (modal_controller.js: `document.addEventListener("keydown", …)`), and
    // would close the whole dialog. A capture-phase listener always runs before
    // any bubble-phase one, whatever order they were registered in, so this
    // keeps the event to itself; preventDefault also stops the browser's own
    // Escape-closes-<dialog> default. Depends on that wiring: if a conek-ui
    // upgrade moves Escape to the capture phase or to the dialog's `cancel`
    // event, re-check CONEK-8085 (Escape must NOT close the Attach dialog while
    // the scanner is up).
    this.onEscape = (event) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      // CONEK-9219 — no way out while the scanned page is loading.
      if (this.isLoading) return
      this.closeModal()
    }
    document.addEventListener("keydown", this.onEscape, true)

    // Any other way of dismissing the dialog must not leave the camera running
    // inside a dialog nobody can see.
    // The dialog itself is closing: nobody is left to hand focus to.
    this.onHostClose = () => this.closeModal({ restoreFocus: false })
    host.addEventListener("close", this.onHostClose)
  }

  restoreHost({ restoreFocus = true } = {}) {
    if (!this.host) return
    this.host.removeEventListener("close", this.onHostClose)
    document.removeEventListener("keydown", this.onEscape, true)
    this.hiddenByScanner.forEach((child) => {
      child.hidden = false
    })
    this.host = null
    this.hiddenByScanner = []
    this.onHostClose = null
    this.onEscape = null
    // The trigger was hidden with the rest of the dialog, so focus fell to
    // <body>; hand it back (a successful scan focuses the input instead).
    if (restoreFocus && this.hasTriggerTarget)
      this.triggerTarget.focus({ preventScroll: true })
  }

  async closeModal({ restoreFocus = true } = {}) {
    this.endLoading()
    if (this.cleanupScanner) {
      const cleanup = this.cleanupScanner
      this.cleanupScanner = null
      await cleanup()
    }
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal)
    }
    // Also when a Turbo render already swapped the modal out with <body>:
    // otherwise the next open() would think a scanner is still up.
    this.modal = null
    this.parts = null
    this.restoreHost({ restoreFocus })
  }

  showFriendlyError(message) {
    const note = document.createElement("div")
    note.setAttribute("role", "status")
    note.className =
      "fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-card text-foreground text-sm border border-border rounded-lg shadow-md px-4 py-2"
    note.textContent = message
    // A showModal() dialog is in the top layer: a note on <body> would sit
    // underneath it, out of sight.
    ;(this.element.closest("dialog") || document.body).appendChild(note)
    setTimeout(() => {
      if (note.parentNode) note.parentNode.removeChild(note)
    }, 4000)
  }
}
